// 전투 액션 VFX 타이밍·모양 상수의 정본(2026-10-27 전투 액션 VFX 레인, docs/ACTION-VFX-REF.md 3절).
// 애니 레인(feat/action-anim)이 클립 판정 프레임·히트스톱을 바꾸면 이 파일만 맞추면 된다. 시간 단위는 틱(60Hz, 영웅 시계 —
// 히트스톱·일시정지에 멈춘다) 또는 초(모듈 수명)로 이름에 적는다. 값은 레퍼런스 프레임 측정(ACTION-VFX-REF 2절)에서 왔다.

/** 기본 공격 칼날 리본(1~4타). 인덱스 = comboStep(0..3) */
export interface TrailShape {
  /** 리본이 덮는 칼날 비율(칼끝 쪽). 1 = 칼자루~칼끝 전체. 초승달 단면이라 칼자루 쪽 절반은 거의 투명하다 */
  readonly blade: number;
  /** 칼끝 밖으로 늘리는 비율(에너지 칼날): 바깥 가장자리 = 칼자루 + (칼끝 − 칼자루) × (1 + reach) */
  readonly reach: number;
  /** 표시 틱(그리기 + 남김 + 디졸브). 레퍼런스 0.18~0.27초 */
  readonly ticks: number;
  /** 흰 심 세기(앞머리·바깥 가장자리) */
  readonly core: number;
  /** 몸통(진홍) 밝기 배율 */
  readonly body: number;
}

export const TRAIL_SHAPES: readonly TrailShape[] = [
  { blade: 0.72, reach: 0.28, ticks: 11, core: 1.0, body: 1.0 },
  { blade: 0.74, reach: 0.3, ticks: 11, core: 1.0, body: 1.0 },
  { blade: 0.8, reach: 0.38, ticks: 13, core: 1.1, body: 1.1 },
  { blade: 0.86, reach: 0.5, ticks: 16, core: 1.25, body: 1.2 },
];

/**
 * 칼끝 궤적(휘두름 시작~끝, 0..1) 가운데 판정 순간 앞쪽으로 남기는 몫. 그보다 앞(들어 올림)은 처음부터 지운다.
 * 1~4타 판정 위치(headStart)는 0.43·0.63·0.79·…라 이 값이 없으면 3타 리본의 대부분이 머리 위 들어 올림이었다
 */
export const TRAIL_LEAD = 0.32;

/** 리본이 다 그려진 뒤 디졸브가 시작되는 시점(표시 틱 대비 비율) */
export const TRAIL_DISSOLVE_FROM = 0.45;

/** 굵은 회전 베기 고리(ACTION-VFX-REF 2절 마무리 4타·폭풍·런처·낙화). 시간은 초, 길이는 m */
export interface RingShape {
  /** 바깥 반경(m). 0이면 이벤트의 판정 반경을 쓴다 */
  readonly radius: number;
  /** 굵은 띠 폭(반경 대비) */
  readonly band: number;
  /** 평면 기울기(rad): 0 = 바닥과 평행, PI/2 = 세로 */
  readonly roll: number;
  /** 높이(m, 영웅 발 기준) */
  readonly y: number;
  /** 수명(초)과 한 바퀴 그리는 시간(초) */
  readonly life: number;
  readonly draw: number;
  /** 쓸어 지나가는 전체 각(rad, 부호 = 방향). 2π보다 조금 크게 그려 이음매를 덮는다 */
  readonly sweep: number;
  readonly intensity: number;
  /** 고리를 가로지르는 직선 베기(마무리타만) */
  readonly cross: boolean;
}

export const RING_SHAPES = {
  /** 마무리 4타 360° 베기: 반경 = 판정 반경(3.6m) */
  finisher: { radius: 0, band: 0.22, roll: 0.3, y: 0.85, life: 0.45, draw: 0.08, sweep: Math.PI * 2.2, intensity: 1.7, cross: true },
  /** 칼날 폭풍 회차(6회): 판정 반경 5m보다 작게 몸 둘레를 감는다 */
  storm: { radius: 2.7, band: 0.2, roll: 0.34, y: 0.75, life: 0.3, draw: 0.07, sweep: Math.PI * 2.15, intensity: 1.35, cross: false },
  /** 칼날 폭풍 마지막 회차: 판정 반경 */
  stormFinal: { radius: 0, band: 0.16, roll: 0.12, y: 0.6, life: 0.42, draw: 0.09, sweep: Math.PI * 2.2, intensity: 1.5, cross: false },
  /** 천공 올려베기: 앞쪽에 세로로 선 고리 */
  launcher: { radius: 2.4, band: 0.2, roll: 1.25, y: 1.0, life: 0.36, draw: 0.07, sweep: Math.PI * 1.6, intensity: 1.6, cross: false },
  /** 붉은 낙화 착지: 바닥과 평행한 큰 고리 */
  fall: { radius: 0, band: 0.2, roll: 0.04, y: 0.3, life: 0.5, draw: 0.08, sweep: Math.PI * 2.2, intensity: 1.7, cross: false },
  /** 공중 연타: 작은 기울어진 고리 */
  juggle: { radius: 1.4, band: 0.22, roll: 0.6, y: 0, life: 0.24, draw: 0.05, sweep: Math.PI * 2.1, intensity: 1.2, cross: false },
} as const satisfies Record<string, RingShape>;

/** 타격 방사선(흰 별 줄기). 시간 초, 길이 m */
export const HIT_BURST = {
  /** 줄 수: 일반 / 강타(치명·마무리·스킬) */
  rays: 6, raysHeavy: 8,
  /** 줄 길이(m) */
  len: 0.7, lenHeavy: 1.05,
  /** 줄 폭(m, 가장 두꺼운 곳) */
  width: 0.09, widthHeavy: 0.13,
  life: 0.11, lifeHeavy: 0.14,
  /** 한 판정 순간(시뮬레이션 틱)에 띄우는 방사선 상한(배율 1) */
  perTick: 4,
  /** 섬광 약화: 밝기·길이 배율 */
  reduceGain: 0.5, reduceLen: 0.7,
} as const;

/** 잔상(돌진·런처·낙화 도약): 몸 높이 세로 띠. 초 */
export const AFTERIMAGE = {
  /** 표본 수명(초): 이보다 오래된 표본은 띠에서 빠진다 */
  life: 0.3,
  /** 띠를 그리는 최대 시간(틱) */
  activeTicks: 40,
  /** 띠 높이(m) */
  y0: 0.15, y1: 1.7,
} as const;

/** 저사양(입자 배율 < LOW_SCALE)에서는 고리 겹을 줄이고 방사선을 덜 낸다(모양은 유지) */
export const LOW_SCALE = 0.6;
